import React, { useEffect, useState } from "react";
import { api, extractError } from "../lib/api";
import { formatIndiaDateTime } from "../lib/indiaTime";
import { weighPhotoDisplaySrc } from "../lib/localScaleAgent";
import { formatWeightKg } from "../lib/weightUnits";
import { GatePhotoThumb } from "./GatePassPhotos";

/**
 * Admin / weighbridge / purchase evidence: gate photos + weigh edits + camera stills.
 */
export default function WeighEvidencePanel({
  purchaseId = null,
  dispatchId = null,
  /** When provided, skip refetch (e.g. purchase detail already loaded the doc). */
  doc: docProp = null,
  showGatePhotos = true,
  className = "",
  testId = "weigh-evidence",
  title = "Weigh history & photos",
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fetchedDoc, setFetchedDoc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (docProp) {
      setFetchedDoc(null);
      setLoading(false);
      setError("");
      return undefined;
    }
    if (!purchaseId && !dispatchId) {
      setFetchedDoc(null);
      return undefined;
    }
    setLoading(true);
    setError("");
    const url = purchaseId ? `/purchases/${purchaseId}` : `/outward-dispatches/${dispatchId}`;
    api
      .get(url)
      .then(({ data }) => {
        if (!cancelled) setFetchedDoc(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setFetchedDoc(null);
          setError(extractError(err) || "Failed to load weigh evidence");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [purchaseId, dispatchId, docProp]);

  if (!purchaseId && !dispatchId && !docProp) return null;

  if (!docProp && loading) {
    return (
      <div className={`text-xs text-muted-foreground py-2 ${className}`} data-testid={testId}>
        Loading weigh history &amp; photos…
      </div>
    );
  }

  if (!docProp && error) {
    return (
      <div className={`text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 ${className}`}>
        {error}
      </div>
    );
  }

  const doc = docProp || fetchedDoc;
  if (!doc) return null;

  const events = extractWeighEvidenceEvents(doc);
  const gallery = collectPurchaseMedia(doc, events);
  const gateItems = showGatePhotos ? gallery.filter((item) => item.group === "gate") : [];
  const otherItems = gallery.filter((item) => item.group === "other");
  const hasWeighPhotos = events.some((ev) => ev.cam1 || ev.cam2);

  return (
    <div className={`space-y-4 ${className}`} data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{title}</p>
        <p className="text-[10px] text-muted-foreground font-mono">{doc.code}</p>
      </div>

      {gateItems.length > 0 ? (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Gate photos</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {gateItems.map((item) => (
              <MediaCard key={item.key} item={item} />
            ))}
          </div>
        </div>
      ) : null}

      {events.length > 0 ? (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Weigh events</p>
          <ul className="space-y-3">
            {events.map((ev, idx) => (
              <li
                key={`${ev.at || "x"}-${idx}`}
                className="rounded-md border border-border bg-background/70 p-2.5 space-y-2"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div>
                    <p className="text-xs font-medium">
                      {ev.label}
                      {ev.weight_qtl != null ? (
                        <span className="tabular text-muted-foreground font-normal">
                          {" "}
                          · {formatWeightKg(ev.weight_qtl)}
                        </span>
                      ) : null}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {formatIndiaDateTime(ev.at) || ev.at || "—"}
                      {ev.by ? ` · ${ev.by}` : ""}
                      {ev.source ? ` · ${ev.source}` : ""}
                    </p>
                  </div>
                  {ev.state ? (
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{ev.state}</span>
                  ) : null}
                </div>
                {ev.note ? <p className="text-[11px] text-muted-foreground">{ev.note}</p> : null}
                {ev.cam1 || ev.cam2 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {ev.cam1 ? (
                      <PhotoThumb
                        src={ev.cam1}
                        label="Angle 1"
                        caption={photoCaption(ev.cam1, "Captured at weighbridge")}
                      />
                    ) : (
                      <p className="text-[10px] text-muted-foreground self-center">No angle 1 photo</p>
                    )}
                    {ev.cam2 ? (
                      <PhotoThumb
                        src={ev.cam2}
                        label="Angle 2"
                        caption={photoCaption(ev.cam2, "Captured at weighbridge")}
                      />
                    ) : (
                      <p className="text-[10px] text-muted-foreground self-center">No angle 2 photo</p>
                    )}
                  </div>
                ) : (
                  <p className="text-[10px] text-muted-foreground">No camera stills on this weigh event.</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {otherItems.length > 0 ? (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Other images</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {otherItems.map((item) => (
              <MediaCard key={item.key} item={item} />
            ))}
          </div>
        </div>
      ) : null}

      {gallery.length === 0 && events.length === 0 ? (
        <p className="text-xs text-muted-foreground">No photos or weigh events linked to this entry yet.</p>
      ) : null}

      {events.length > 0 && !hasWeighPhotos && gateItems.length === 0 && otherItems.length === 0 ? (
        <p className="text-xs text-muted-foreground">Weigh weights are recorded, but no camera captures were saved.</p>
      ) : null}
    </div>
  );
}

function MediaCard({ item }) {
  return (
    <div className="min-w-0">
      <GatePhotoThumb src={item.src} label={item.label} />
      {item.caption ? (
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{item.caption}</p>
      ) : null}
    </div>
  );
}

function PhotoThumb({ src, label, caption }) {
  const [href, setHref] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);

    const resolve = async (raw) => {
      if (!raw) {
        setHref("");
        return;
      }
      if (/^https?:\/\//i.test(raw) && !/X-Amz-/i.test(raw)) {
        setHref(raw);
        return;
      }
      if (/^https?:\/\//i.test(raw) && /X-Amz-/i.test(raw)) {
        setHref(raw);
        return;
      }
      if (raw.startsWith("media://") || String(raw).includes("/weighbridge/media/")) {
        setHref(weighPhotoDisplaySrc(raw));
        return;
      }
      const { data } = await api.get("/weighbridge/photo-url", { params: { uri: raw } });
      if (!cancelled) setHref(weighPhotoDisplaySrc(data?.url || "") || data?.url || "");
    };

    resolve(String(src || "").trim()).catch(() => {
      if (!cancelled) {
        setHref("");
        setFailed(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [src]);

  const refreshFromPath = async () => {
    const raw = String(src || "").trim();
    if (!raw) {
      setFailed(true);
      return;
    }
    try {
      const pathOnly = raw.split("?")[0];
      const { data } = await api.get("/weighbridge/photo-url", { params: { uri: pathOnly } });
      if (data?.url) {
        setFailed(false);
        setHref(weighPhotoDisplaySrc(data.url) || data.url);
      } else {
        setFailed(true);
      }
    } catch {
      setFailed(true);
    }
  };

  if (failed) {
    return <p className="text-[10px] text-muted-foreground">{label}: unavailable</p>;
  }
  if (!href) {
    return <p className="text-[10px] text-muted-foreground">{label}: loading…</p>;
  }
  return (
    <a href={href} target="_blank" rel="noreferrer" className="block min-w-0">
      <img
        src={href}
        alt={label}
        className="h-28 w-full object-cover rounded border border-border bg-muted"
        onError={() => {
          refreshFromPath();
        }}
      />
      <span className="text-[10px] text-muted-foreground">{label}</span>
      {caption ? (
        <span className="block text-[10px] uppercase tracking-widest text-muted-foreground">{caption}</span>
      ) : null}
    </a>
  );
}

/** True for stock/demo Unsplash placeholders — not a live weighbridge capture. */
export function isPlaceholderPhotoUrl(url) {
  const raw = String(url || "").trim();
  if (!raw) return true;
  return /unsplash\.com/i.test(raw);
}

/** Caption for real captures; never labels live images as MOCKED. */
export function photoCaption(url, realLabel = "Captured at weighbridge") {
  if (!url || isPlaceholderPhotoUrl(url)) return "Placeholder image";
  return realLabel;
}

export function collectPurchaseMedia(doc, events = null) {
  const evs = events || extractWeighEvidenceEvents(doc);
  const items = [];
  const seen = new Set();

  const push = (src, { label, group, caption }) => {
    const raw = String(src || "").trim();
    if (!raw || seen.has(raw)) return;
    if (isPlaceholderPhotoUrl(raw) && group !== "other") return;
    seen.add(raw);
    items.push({
      key: raw,
      src: raw,
      label,
      group,
      caption: caption || photoCaption(raw, group === "gate" ? "Captured at gate" : "Captured at weighbridge"),
    });
  };

  const plate = doc.number_plate_snapshot_uri || doc.gate_pass?.number_plate_snapshot_uri;
  push(plate, { label: "Number plate", group: "gate", caption: photoCaption(plate, "Captured at gate") });

  const vehiclePhotos = Array.isArray(doc.vehicle_photos)
    ? doc.vehicle_photos
    : Array.isArray(doc.gate_pass?.vehicle_photos)
      ? doc.gate_pass.vehicle_photos
      : [];
  vehiclePhotos.forEach((ph, i) => {
    const src = ph?.uri || ph?.url;
    push(src, {
      label: ph?.label || `Gate vehicle ${i + 1}`,
      group: "gate",
      caption: photoCaption(src, "Captured at gate"),
    });
  });

  for (const ev of evs) {
    if (ev.cam1) {
      push(ev.cam1, {
        label: `${ev.label} · Angle 1`,
        group: "weigh",
        caption: photoCaption(ev.cam1, "Captured at weighbridge"),
      });
    }
    if (ev.cam2) {
      push(ev.cam2, {
        label: `${ev.label} · Angle 2`,
        group: "weigh",
        caption: photoCaption(ev.cam2, "Captured at weighbridge"),
      });
    }
  }

  const primary = String(doc.vehicle_snapshot_url || "").trim();
  if (primary && !seen.has(primary)) {
    push(primary, {
      label: "Primary vehicle snapshot",
      group: isPlaceholderPhotoUrl(primary) ? "other" : "weigh",
      caption: photoCaption(primary, "Captured at weighbridge"),
    });
  }

  return items;
}

export function extractWeighEvidenceEvents(doc) {
  const history = Array.isArray(doc.history) ? doc.history : [];
  const events = [];

  for (const h of history) {
    const hasWeight = h.weight_qtl != null && h.weight_qtl !== "";
    const cam1 = h.camera1_snapshot_url || h.camera1_snapshot_uri || null;
    const cam2 = h.camera2_snapshot_url || h.camera2_snapshot_uri || null;
    const note = String(h.note || "");
    const state = String(h.state || "");
    const weighLike =
      hasWeight ||
      cam1 ||
      cam2 ||
      /weigh|tare|gross|mid|correction|bridge/i.test(note) ||
      /WEIGH_|TARE|GROSS|MID/i.test(state);

    if (!weighLike) continue;

    let label = state || "Weigh event";
    if (/gross/i.test(note) || state === "WEIGH_GROSS" || (hasWeight && /Gross/i.test(note))) {
      label = "Gross weigh";
    } else if (/tare|final tare/i.test(note) || state === "WEIGH_TARE") {
      label = "Tare weigh";
    } else if (/mid/i.test(note) || state === "WEIGH_MID") {
      label = "Mid weigh";
    } else if (/correct/i.test(note)) {
      label = "Weight correction";
    } else if (hasWeight) {
      label = "Bridge weight";
    } else if (cam1 || cam2) {
      label = "Camera capture";
    }

    events.push({
      at: h.at,
      by: h.by,
      state,
      note,
      source: h.source,
      weight_qtl: hasWeight ? h.weight_qtl : null,
      label,
      cam1,
      cam2,
    });
  }

  // Legacy rows: weights saved on columns but history JSON never flushed.
  if (!events.some((ev) => ev.weight_qtl != null)) {
    const snap = String(doc.vehicle_snapshot_url || "").trim();
    const snapOk = snap && !isPlaceholderPhotoUrl(snap) ? snap : null;
    if (doc.gross_weight_qtl != null && doc.gross_weight_qtl !== "") {
      events.push({
        at: doc.gross_weighed_at || doc.created_at || null,
        by: doc.gross_weighed_by || null,
        state: "WEIGH_GROSS",
        note: "Gross weigh-in",
        source: null,
        weight_qtl: doc.gross_weight_qtl,
        label: "Gross weigh",
        cam1: snapOk,
        cam2: null,
      });
    }
    for (const seg of Array.isArray(doc.loose_weigh_segments) ? doc.loose_weigh_segments : []) {
      if (seg?.weight_qtl == null || seg.weight_qtl === "") continue;
      const kind = String(seg.kind || "").toLowerCase();
      if (kind === "mid") {
        events.push({
          at: seg.at || null,
          by: seg.by || null,
          state: "WEIGH_MID",
          note: seg.segment_net_qtl != null ? `Mid weight — segment net ${seg.segment_net_qtl} qtl` : "Mid weight",
          source: seg.source || null,
          weight_qtl: seg.weight_qtl,
          label: "Mid weigh",
          cam1: null,
          cam2: null,
        });
      } else if (kind === "tare") {
        events.push({
          at: seg.at || doc.tare_weighed_at || null,
          by: seg.by || doc.tare_weighed_by || null,
          state: "WEIGH_TARE",
          note: doc.net_weight_qtl != null ? `Final tare — total net ${doc.net_weight_qtl} qtl` : "Final tare",
          source: seg.source || null,
          weight_qtl: seg.weight_qtl,
          label: "Tare weigh",
          cam1: null,
          cam2: null,
        });
      }
    }
    if (
      doc.tare_weight_qtl != null &&
      doc.tare_weight_qtl !== "" &&
      !events.some((ev) => ev.label === "Tare weigh")
    ) {
      events.push({
        at: doc.tare_weighed_at || null,
        by: doc.tare_weighed_by || null,
        state: "WEIGH_TARE",
        note: doc.net_weight_qtl != null ? `Tare recorded — net ${doc.net_weight_qtl} qtl` : "Tare recorded",
        source: null,
        weight_qtl: doc.tare_weight_qtl,
        label: "Tare weigh",
        cam1: null,
        cam2: null,
      });
    }
  }

  // Newest first
  return events.slice().reverse();
}
